import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { autoScrollForElements, autoScrollWindowForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { canDropTaskIntoProject, isTaskData } from '~/modules/task/helpers/drag-and-drop';
import { isCoarsePointerDevice } from '~/modules/task/helpers/mobile-drag-indicator';
import { scrollStatusIntoView } from '~/modules/task/helpers/panel-scroll-registry';
import type { Task } from '~/modules/task/types';

interface UsePanelDropTargetOptions {
  panelRef: RefObject<HTMLDivElement | null>;
  projectId: string;
  tasks: Task[];
}

/**
 * Hook to set up drag-and-drop target behavior for a task panel.
 * Handles highlighting when dragging tasks from other projects.
 *
 * Registers on the outer panel wrapper so the drop target is live in both
 * expanded and collapsed states.
 */
export function usePanelDropTarget({ panelRef, projectId, tasks }: UsePanelDropTargetOptions) {
  const [highlightProject, setHighlightProject] = useState(false);
  // Use ref so getData() always reads the latest tasks without re-registering listeners
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  useEffect(() => {
    if (!panelRef.current) return;

    return dropTargetForElements({
      element: panelRef.current,
      getData: () => ({ dragItem: true, item: { projectId, tasks: tasksRef.current }, type: 'panel', itemType: 'project' }),
      canDrop: ({ source: { data } }) => {
        if (!isTaskData(data)) return false;
        return canDropTaskIntoProject(data.item.projectId, projectId);
      },
      onDragEnter: ({ source: { data } }) => {
        if (!isTaskData(data) || data.item.projectId === projectId) return;
        setHighlightProject(true);
        // Bring this panel's source-status band into view so the drag gets between-card drop
        // targets; fires once per panel entry (moving over child cards keeps the panel in the
        // drop chain). Skipped on coarse pointers: they resolve drops via the rect-derived
        // mobile indicator, and a window scroll mid-touch-drag would yank the page.
        if (!isCoarsePointerDevice()) scrollStatusIntoView(projectId, data.item.status);
      },
      onDragLeave: () => setHighlightProject(false),
      onDrop: () => setHighlightProject(false),
    });
  }, [projectId, panelRef]);

  return { highlightProject };
}

/**
 * Attach vertical auto-scroll to a scrollable element during drag operations.
 * Lives separately from `usePanelDropTarget` because the scroll viewport only
 * exists in the expanded state, while the drop target must be live in both.
 *
 * When `windowScroll` is set (mobile / windowScroll branches where the page
 * itself scrolls, so no element viewport is mounted), falls back to window
 * auto-scroll so dragging near the viewport edge still scrolls the page.
 * `windowScroll` is a dependency so the registration flips to the correct target
 * if the branch changes (e.g. crossing the mobile breakpoint) while mounted.
 */
export function usePanelAutoScroll(scrollRef: RefObject<HTMLElement | null>, windowScroll: boolean) {
  useEffect(() => {
    if (!windowScroll && scrollRef.current) {
      return autoScrollForElements({ element: scrollRef.current, getAllowedAxis: () => 'vertical' });
    }
    return autoScrollWindowForElements({ getAllowedAxis: () => 'vertical' });
  }, [scrollRef, windowScroll]);
}
